import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { observer } from 'mobx-react';
import { Box, Grid, makeStyles, Typography } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { useUserStore } from '@roc/feature-app-core';
import { LoanDrawsList, useDrawsStore, AddDrawDocumentDialog } from '@roc/feature-draws';
import { useGeneralTasksStore } from '@roc/feature-general-tasks';
import { useLoanStore } from '@roc/feature-loans';
import { Button, Paper, PaperBorderType } from '@roc/ui';
import { LoanActionButtons } from './components/action-buttons/loanActionButtons';
import KeyInformationCollapse from './components/key-information-collapse/key-information-collapse';
import LoanActors from './components/loan-actors/loan-actors';
import { VirtualScroll } from './components/common/virtualScroll';
import LoanValuesChart from 'libs/feature-draws/src/draws/loanDrawsDashboard/components/loanValuesChart';

const useStyles = makeStyles(() => ({
  gridContainer: {
    height: 'fit-content',
    padding: '10px',
  },
  gridPaperContainer: {
    margin: '0px',
    padding: '0px !important',
    '& > .MuiGrid-item': {
      paddingLeft: '0px',
      paddingRight: '0px',
      '&:first-child > .MuiGrid-item': {
        height: '100%',
        '&:first-child > .MuiPaper-root': {
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          '& > .MuiBox-root:last-child': {
            marginBottom: '8%',
          },
        },
      },
      '& .MuiPaper-root': {
        borderColor: '#E1E1E1',
      },
    },
  },
  drawGridHeader: {
    padding: 16,
  },
  button: {
    border: `1px solid rgba(40, 83, 145, 1)`,
    borderRadius: 4,
    backgroundColor: 'transparent',
    fontSize: 14,
    fontWeight: 500,
    color: 'rgba(40, 83, 145, 1)',
    padding: '4px 8px',
    marginLeft: 10,
    textTransform: 'capitalize',
    '&:hover': {
      backgroundColor: 'rgba(237, 237, 237, 1)'
    }
  },
}));

export const DrawsInternal = observer(() => {
  const classes = useStyles();

  const { loanStore } = useLoanStore();

  const { userStore } = useUserStore();
  const { drawStore,
    drawStore: { drawList, drawActionsStore },
  } = useDrawsStore();
  const { generalTasksStore } = useGeneralTasksStore();

  const [specialistsDropdown, setSpecialistsDropdown] = useState();
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);

  const params = useParams<{ loanId: string }>();
  const loanId = params.loanId;
  const properties = loanStore?.loanDetails?.properties || [];
  const ACH_CONTENT_FORM = 'ACH Consent Form';

  useEffect(() => {
    userStore.getUserInfoAndEntitlements();
  }, [userStore]);

  useEffect(() => {
    if (loanStore.loanDetails?.loanId !== loanId) {
      drawStore.fetchDocumentStatus(loanId, ACH_CONTENT_FORM);
      drawStore.getBankInfo(Number(loanId));
      drawStore.fetchWireAuthorizationLink(Number(loanId));
    }
  }, [loanStore, loanId]);

  useEffect(() => {
    drawStore.fetchDraws(loanId, true);

    loanStore.fetchLoanDetails(loanId);
  }, [loanStore, loanId]);

  useEffect(() => {
    drawStore.fetchDraws(params.loanId, true);
    return () => {
      drawStore.resetDraws();
    };
  }, [drawStore, loanId]);

  useEffect(() => {
    drawActionsStore.getFreezeFundingStatus(loanId);
    generalTasksStore.setLoanId(parseInt(loanId));
    generalTasksStore.fetchUserInfoAndAssigneeOptions(loanId);
  }, [drawActionsStore, generalTasksStore, loanId])


  useEffect(() => {
    async function fetchSpecialistsDropdown() {
      await loanStore.fetchSpecialistsDropdown();
      setSpecialistsDropdown(loanStore.specialistsDropdown);
    }

    loanStore.fetchLoanDetails(loanId);
    fetchSpecialistsDropdown();
  }, [loanStore, loanId]);
  const backInformationData = {
    bankInformation: drawStore.bankInformation,
    wireAuthorizationLink: drawStore.wireAuthorizationLink,
  }
  return (
    <VirtualScroll>
      <Grid container className={classes.gridContainer} spacing={2}>
        <KeyInformationCollapse backInformationData={backInformationData} loan={loanStore.loanDetails} achContentFormStatus={drawStore?.documentStatus[ACH_CONTENT_FORM]} />
        <Grid
          item
          container
          className={classes.gridPaperContainer}
          direction="row"
          spacing={1}
          justifyContent='space-between'
        >
          <Grid item xs={12} md={6}>
            <LoanValuesChart
              chRemaining={
                loanStore?.loanDetails?.calculatedLoanData?.constructionHoldbackRemaining
                  ? loanStore?.loanDetails?.calculatedLoanData?.constructionHoldbackRemaining
                  : 0
              }
              chReimbursedToDate={
                loanStore?.loanDetails?.calculatedLoanData?.constructionHoldbackFunded
                  ? loanStore?.loanDetails?.calculatedLoanData?.constructionHoldbackFunded
                  : 0
              }
              borderType={PaperBorderType.SOLID_BORDER}
            />
          </Grid>
          <Grid
            item
            container
            direction="column"
            spacing={1}
            xs={12}
            md={6}
          >
            <Grid item>
              <LoanActors loan={loanStore.loanDetails} specialistsDropdown={specialistsDropdown} />
            </Grid>
            <Grid item>
              <LoanActionButtons loanId={loanId} />
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Paper borderType={PaperBorderType.SOLID_BORDER} titleComponent={
              <Grid container direction='row' justifyContent='space-between' className={classes.drawGridHeader}>
                <Grid item>
                  <Typography variant='h5' component='h5'>Draws</Typography >
                </Grid >
                <Grid item>
                  <Button
                    testId="add-document"
                    variant="contained"
                    className={classes.button}
                    onClick={() => { setIsUploadDialogOpen(true) }
                    }
                  >
                    <AddIcon /> Add document
                  </Button>
                </Grid>
              </Grid>
            }>
              <LoanDrawsList loanId={`${loanId}`} properties={properties} />
            </Paper>
          </Grid>
        </Grid>
        <AddDrawDocumentDialog store={drawStore} loanDetails={loanStore.loanDetails} drawList={drawList} properties={properties} open={isUploadDialogOpen} onClose={setIsUploadDialogOpen} />
      </Grid>
      <Box style={{ height: "80px" }}>
        {/* Placeholder to give space to conversations button*/}
      </Box>
    </VirtualScroll >
  );
});
